"use client";
import { useEffect, useMemo, useRef } from "react";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import MonarchResolver from "@/monarch/MonarchResolver";
import type { UmbrellaPromoPageTestResult } from "@/monarch/rules/umbrellaPromoPage/types";
import FullPageSkeleton from "@/components/ui/skeleton/variants/fullPageSkeleton";

interface MonarchContentWrapperProps {
  children: React.ReactNode;
}

const SECTION_IDS = {
  COURSES: "courses-programs-promo-section",
  EXEC_ED: "executive-education-promo-section",
} as const;

type PromoSectionType = typeof SECTION_IDS.COURSES | typeof SECTION_IDS.EXEC_ED;

const isValidPromoSection = (
  id: string | null | undefined,
): id is PromoSectionType =>
  id === SECTION_IDS.COURSES || id === SECTION_IDS.EXEC_ED;

const isValidBannerGroup = (group: { item?: unknown } | undefined): boolean =>
  !!(
    group?.item &&
    typeof group.item === "object" &&
    Object.keys(group.item).length > 0
  );

const isValidProductList = (
  productList: { id?: string; products?: unknown[] } | undefined,
): boolean =>
  !!(
    productList?.id &&
    isValidPromoSection(productList.id) &&
    productList.products &&
    productList.products.length > 0
  );

const findInsertionPoint = (
  parent: Element,
  isValidPromoSection: (id: string | null | undefined) => boolean,
): Node | null => {
  const allChildren = Array.from(parent.children);
  for (const child of allChildren) {
    if (isValidPromoSection(child.id)) {
      let nextSibling = child.nextSibling;
      while (
        nextSibling &&
        (nextSibling as Element).id &&
        isValidPromoSection((nextSibling as Element).id)
      ) {
        nextSibling = nextSibling.nextSibling;
      }
      return nextSibling;
    }
  }
  return null;
};

const findFirstNonPromoSection = (
  parent: Element,
  isValidPromoSection: (id: string | null | undefined) => boolean,
): Element | undefined =>
  Array.from(parent.children).find((child) => !isValidPromoSection(child.id));

const isValidNode = (node: Node | null, parent: Element): boolean => {
  if (!node) return false;
  if (node.parentNode !== parent) return false;
  if (node.nodeType === Node.ELEMENT_NODE) {
    return Array.from(parent.children).includes(node as Element);
  }
  return true;
};

interface ContentReordererProps {
  children: React.ReactNode;
  testResult: UmbrellaPromoPageTestResult | null;
}

function ContentReorderer({ children, testResult }: ContentReordererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasReorderedRef = useRef(false);

  const promoSectionOrder = testResult?.data?.promoSectionOrder;
  const banners = testResult?.data?.banners;
  const productLists = testResult?.data?.productLists;

  const hasTwoBanners = useMemo(
    () =>
      Array.isArray(banners) && banners.filter(isValidBannerGroup).length === 2,
    [banners],
  );

  const hasTwoProductLists = useMemo(
    () =>
      Array.isArray(productLists) &&
      productLists.filter(isValidProductList).length === 2,
    [productLists],
  );

  const sectionOrder = useMemo(() => {
    if (!promoSectionOrder || promoSectionOrder.length === 0) {
      return [];
    }
    return promoSectionOrder
      .map((item) => item.promoSectionType)
      .filter(isValidPromoSection);
  }, [promoSectionOrder]);

  // Only reorder if promoSectionOrder is active and has exactly 2 distinct elements
  // Also requires EITHER 2 valid banners OR 2 valid product lists to ensure data consistency
  const shouldReorder = useMemo(() => {
    // Check if promoSectionOrder has exactly 2 distinct valid sections
    const hasTwoDistinctSections =
      sectionOrder.length === 2 && sectionOrder[0] !== sectionOrder[1];

    // Reorder if we have the section order and either banners or product lists for both sections
    return hasTwoDistinctSections && (hasTwoBanners || hasTwoProductLists);
  }, [sectionOrder, hasTwoBanners, hasTwoProductLists]);

  // Only reorder sections if promoSectionOrder is active with 2 distinct elements
  // Otherwise, sections render normally from shortcode without interference
  useEffect(() => {
    if (!containerRef.current || !shouldReorder || hasReorderedRef.current) {
      return;
    }

    const reorderSections = (): boolean => {
      const container = containerRef.current;
      if (!container) return false;

      const coursesSection = container.querySelector(
        `#${SECTION_IDS.COURSES}`,
      ) as HTMLElement | null;
      const execEdSection = container.querySelector(
        `#${SECTION_IDS.EXEC_ED}`,
      ) as HTMLElement | null;

      if (!coursesSection || !execEdSection) return false;

      const sectionsMap = new Map<PromoSectionType, HTMLElement>([
        [SECTION_IDS.COURSES, coursesSection],
        [SECTION_IDS.EXEC_ED, execEdSection],
      ]);

      const parent = coursesSection.parentElement;
      if (!parent) return false;

      const orderedSections = sectionOrder
        .map((sectionType) => sectionsMap.get(sectionType))
        .filter((section): section is HTMLElement => !!section);

      if (orderedSections.length === 0) return false;

      const insertBefore = findInsertionPoint(parent, isValidPromoSection);

      orderedSections.forEach((section) => section.remove());

      orderedSections.forEach((section, index) => {
        if (index === 0) {
          if (insertBefore && isValidNode(insertBefore, parent)) {
            try {
              parent.insertBefore(section, insertBefore);
            } catch {
              const fallback = findFirstNonPromoSection(
                parent,
                isValidPromoSection,
              );
              fallback
                ? parent.insertBefore(section, fallback)
                : parent.appendChild(section);
            }
          } else {
            const fallback = findFirstNonPromoSection(
              parent,
              isValidPromoSection,
            );
            fallback
              ? parent.insertBefore(section, fallback)
              : parent.appendChild(section);
          }
        } else {
          const previousSection = orderedSections[index - 1];
          const nextSibling = previousSection.nextSibling;
          if (nextSibling && isValidNode(nextSibling, parent)) {
            try {
              parent.insertBefore(section, nextSibling);
            } catch {
              parent.appendChild(section);
            }
          } else {
            parent.appendChild(section);
          }
        }
      });

      return true;
    };

    if (reorderSections()) {
      hasReorderedRef.current = true;
      return;
    }

    const timeoutId = setTimeout(() => {
      if (reorderSections()) {
        hasReorderedRef.current = true;
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [shouldReorder, sectionOrder]);

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .monarch-content-wrapper > #courses-programs-promo-section:not(:first-child),
            .monarch-content-wrapper > #executive-education-promo-section:not(:first-child) {
              margin-top: 4rem !important;
            }
          `,
        }}
      />
      <div ref={containerRef} className="monarch-content-wrapper">
        {children}
      </div>
    </>
  );
}

/**
 * Wrapper component that reorders promo sections based on Monarch promoSectionOrder.
 * Only applies to /offers page (checked in MainPageRender.tsx).
 */
export default function MonarchContentWrapper({
  children,
}: MonarchContentWrapperProps) {
  const renderContentWithReorder = (
    testResult: UmbrellaPromoPageTestResult | null,
  ) => <ContentReorderer testResult={testResult}>{children}</ContentReorderer>;

  const monarchContentComponent = (
    <MonarchDataConsumer<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      render={(testResult) => renderContentWithReorder(testResult || null)}
    />
  );

  return (
    <MonarchResolver<UmbrellaPromoPageTestResult>
      experimentKey="useUmbrellaPromoPage"
      Loader={<FullPageSkeleton />}
      variants={{
        control: monarchContentComponent,
        test: monarchContentComponent,
      }}
      fallbackVariant="control"
    />
  );
}
